<script setup>
/**
 * FormControlFilesUsageItem
 * Resolves the breadcrumb path for a file usage instance:
 * Component > Record Title > Field Label
 */
import {ref, computed, watch, onMounted} from 'vue'
import {useRouter} from 'vue-router'
import {useComponentsStore} from '@/stores/components'
import {useItemDataStore} from '@/stores/itemData'
import Button from '@/common/Button.vue'

const props = defineProps({
    item: {
        type: Object,
        required: true
    }
})

const router = useRouter()

const componentsStore = useComponentsStore()
const itemDataStore = useItemDataStore()

const column = ref(null)
const componentItem = ref(null)
const itemTitle = ref('')

const formControlLabel = computed(() => column.value?.title || '')

const component = computed(() => {
    if (!column.value) return null
    return componentsStore.items
        .find(o => o.id === column.value.modulecomponentsID || o.id === column.value.modulecomponentsId)
})

const isPageComponent = computed(() => component.value?.tableName?.startsWith('component_'))

const componentTitle = computed(() => {
    if (!componentItem.value) return ''
    return isPageComponent.value
        ? `Content #${componentItem.value.contentId}`
        : component.value?.title
})

const nameField = computed(() => component.value ? componentsStore.nameField(component.value.id) : null)

/**
 * Fetches the chain of data required to display the usage location.
 */
const resolveUsageData = async () => {
    try {
        // 1. Get Column Structure
        column.value = await itemDataStore.get({
            tableName: 'modulestructure',
            id: props.item.structureId
        })

        if (!component.value) return

        // 2. Get the specific record using the file
        componentItem.value = await itemDataStore.get({
            tableName: component.value.tableName,
            id: props.item.itemId
        })

        if (!componentItem.value) return

        // 3. Resolve the title
        if (isPageComponent.value) {
            const contentRecord = await itemDataStore.get({
                tableName: 'content',
                id: componentItem.value.contentId
            })
            itemTitle.value = contentRecord?.title || ''
        } else {
            itemTitle.value = componentItem.value[nameField.value] || ''
        }
    } catch (error) {
        console.error("Failed to resolve file usage item:", error)
    }
}

const goToItem = () => {
    if (isPageComponent.value) {
        router.push({
            name: 'form',
            params: {component: 'content', id: componentItem.value.contentId}
        })
    } else if (component.value?.tableName === 'templates_templates') {
        router.push({
            name: 'themesEditor',
            query: {section: 'templates_templates', id: props.item.itemId}
        })
    } else {
        router.push({
            name: 'form',
            params: {component: component.value.tableName, id: props.item.itemId}
        })
    }
}

onMounted(() => {
    resolveUsageData()
})

// Re-run if the item object changes
watch(() => props.item, () => {
    resolveUsageData()
}, {deep: true})
</script>

<template>
    <div class="d-flex align-items-center">
        <div class="flex-fill small">
            <span>{{ componentTitle }}</span>
            <span class="mx-1 text-secondary">&gt;</span>

            <template v-if="itemTitle">{{ itemTitle }}</template>
            <em v-else class="text-muted">untitled</em>

            <span class="mx-1 text-secondary">&gt;</span>
            <span>{{ formControlLabel }}</span>
        </div>

        <div class="ms-2">
            <Button class="btn-light btn-sm" @click="goToItem">
                <i class="bi-pencil-square"/>
            </Button>
        </div>
    </div>
</template>

<style scoped>
em {
    padding: 0 3px;
    font-style: italic;
}
</style>